import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { advocaciaSocios, advocaciaBlogPosts, LawBlogPost } from '../../shared/data/advocaciaData';
import { Scale, ShieldCheck, FileText, BookOpen, ArrowRight, ChevronRight, Phone, MapPin, Search, CheckCircle2, Clock } from 'lucide-react';

interface LawDemoProps {
  prospect: Prospect;
}

export const LawDemo: React.FC<LawDemoProps> = ({ prospect }) => {
  const [selectedPost, setSelectedPost] = useState<LawBlogPost | null>(null);
  const [intakeStep, setIntakeStep] = useState(1);
  const [intakeArea, setIntakeArea] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900 font-sans selection:bg-[#0F172A] selection:text-white">
      
      {/* Navbar Editorial Nobre - STRICT NO SHADOWS */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 h-24 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <div className="w-12 h-12 rounded-xl bg-[#0F172A] text-[#C5A880] flex items-center justify-center border border-slate-800">
                <Scale className="w-6 h-6" />
             </div>
             <div>
                <h1 className="text-xl font-heading font-bold text-[#0F172A] tracking-tight uppercase leading-none">{prospect.trade_name}</h1>
                <span className="text-[10px] text-slate-500 font-medium tracking-widest uppercase">Advocacia & Conselheiros</span>
             </div>
          </div>

          <nav className="hidden md:flex items-center gap-10 text-xs font-bold uppercase tracking-wider text-slate-600">
             <a href="#atuacao" className="hover:text-[#0F172A] transition-colors">Áreas de Atuação</a>
             <a href="#socios" className="hover:text-[#0F172A] transition-colors">Sócios</a>
             <a href="#blog" className="hover:text-[#0F172A] transition-colors">Artigos & SEO</a>
             <a href="#triagem" className="hover:text-[#0F172A] transition-colors">Avaliação de Caso</a>
          </nav>

          <button className="hidden sm:flex bg-[#0F172A] text-[#C5A880] border border-[#0F172A] px-6 py-3 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-800 transition-colors items-center gap-2">
             <Phone className="w-4 h-4" />
             <span>Consulta Presencial</span>
          </button>
        </div>
      </header>

      {/* Hero Editorial Assimétrico - STRICT NO SHADOWS */}
      <section className="pt-40 pb-24 px-8 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-xl bg-slate-100 border border-slate-200 text-xs font-bold text-[#0F172A] uppercase tracking-widest">
                 <ShieldCheck className="w-4 h-4 text-[#C5A880]" />
                 <span>Excelência Jurídica & Integridade</span>
              </div>

              <h2 className="text-5xl lg:text-7xl font-heading font-normal text-[#0F172A] tracking-tight leading-[1.1]">
                 Segurança para decisões de <br />
                 <span className="italic font-serif text-[#C5A880]">alto impacto.</span>
              </h2>

              <p className="text-base text-slate-600 max-w-xl font-light leading-relaxed">
                 Assessoramos corporações, holdings familiares e executivos em litígios complexos e planejamento tributário estratégico com precisão cirúrgica.
              </p>

              <div className="pt-4 flex flex-wrap gap-4">
                 <a href="#triagem" className="bg-[#0F172A] text-white px-8 py-4 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-slate-800 transition-colors inline-flex items-center gap-3">
                    Submeter Caso para Análise <ArrowRight className="w-4 h-4 text-[#C5A880]" />
                 </a>
              </div>
           </div>

           {/* Imagem Editorial com Moldura de Borda */}
           <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border-2 border-slate-200 p-2 bg-slate-50">
                 <div className="aspect-[4/5] rounded-xl overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=800&auto=format&fit=crop" 
                      alt="Escritório de Advocacia" 
                      className="w-full h-full object-cover grayscale"
                    />
                 </div>
                 <div className="p-4 bg-white border border-slate-200 rounded-xl mt-2 flex items-center justify-between">
                    <div>
                       <p className="text-xs font-bold text-[#0F172A]">Faria Lima, São Paulo</p>
                       <p className="text-[10px] text-slate-400">Atendimento Nacional & Internacional</p>
                    </div>
                    <span className="text-xs font-bold text-[#C5A880] uppercase tracking-wider">OAB/SP Ativa</span>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Áreas de Atuação - Cards Limpos com Borda (Sem Sombras) */}
      <section id="atuacao" className="py-24 px-8 bg-[#F8FAFC]">
         <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-slate-200">
               <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#C5A880]">Práticas Jurídicas</span>
                  <h3 className="text-3xl lg:text-4xl font-heading font-bold text-[#0F172A] mt-2">Áreas de Atuação</h3>
               </div>
               <p className="text-sm text-slate-500 font-light max-w-md mt-4 md:mt-0">
                  Soluções customizadas sob medida para estruturas societárias e patrimoniais.
               </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
               {[
                  { title: 'Direito Tributário & Fiscal', desc: 'Recuperação de créditos, contencioso administrativo e planejamento tributário focado em redução legal de alíquotas.' },
                  { title: 'Holding Familiar & Sucessões', desc: 'Proteção de bens da família, governança de ativos e planejamento sucessório prevenindo conflitos entre herdeiros.' },
                  { title: 'Direito Corporativo & M&A', desc: 'Fusões, aquisições, due diligence minuciosa e elaboração de acordos de acionistas de alta complexidade.' },
               ].map((area, idx) => (
                  <div key={idx} className="bg-white p-8 rounded-xl border border-slate-200 flex flex-col justify-between hover:border-[#0F172A] transition-all group">
                     <div>
                        <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 text-[#0F172A] flex items-center justify-center font-bold mb-6 group-hover:bg-[#0F172A] group-hover:text-[#C5A880] transition-colors">
                           0{idx + 1}
                        </div>
                        <h4 className="font-heading font-bold text-xl text-[#0F172A] mb-3">{area.title}</h4>
                        <p className="text-xs text-slate-600 font-light leading-relaxed mb-6">{area.desc}</p>
                     </div>
                     <span className="text-xs font-bold text-[#C5A880] uppercase tracking-wider flex items-center gap-2">
                        Saber Mais <ChevronRight className="w-4 h-4" />
                     </span>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* Módulo do Blog Integrado & Autoridade SEO (Rank #1 Google) */}
      <section id="blog" className="py-24 px-8 bg-white border-t border-slate-200">
         <div className="max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 pb-6 border-b border-slate-200">
               <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-[#C5A880] flex items-center gap-2">
                     <BookOpen className="w-4 h-4" /> Blog Próprio & SEO de Longo Prazo
                  </span>
                  <h3 className="text-3xl lg:text-4xl font-heading font-bold text-[#0F172A] mt-2">Artigos & Pareceres Jurídicos</h3>
               </div>
               <div className="mt-4 md:mt-0 text-right">
                  <span className="bg-slate-100 text-slate-700 text-xs font-bold px-3 py-1.5 rounded-lg border border-slate-200">
                     Rank #1 no Google em Pesquisa de Holding
                  </span>
               </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
               {advocaciaBlogPosts.map(post => (
                  <div 
                     key={post.id} 
                     onClick={() => setSelectedPost(post)}
                     className="bg-[#F8FAFC] border border-slate-200 p-8 rounded-xl cursor-pointer hover:border-[#0F172A] transition-all flex flex-col justify-between group"
                  >
                     <div>
                        <div className="flex items-center justify-between text-xs text-slate-500 font-medium mb-4">
                           <span className="text-[#C5A880] font-bold uppercase tracking-wider">{post.category}</span>
                           <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {post.readTime}</span>
                        </div>
                        <h4 className="font-heading font-bold text-2xl text-[#0F172A] mb-4 group-hover:text-[#C5A880] transition-colors leading-tight">
                           {post.title}
                        </h4>
                        <p className="text-xs text-slate-600 font-light leading-relaxed mb-6">
                           {post.summary}
                        </p>
                     </div>
                     <div className="pt-4 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                        <span>Por {post.author}</span>
                        <span className="font-bold text-[#0F172A] flex items-center gap-1">Ler Artigo Completo <ChevronRight className="w-4 h-4" /></span>
                     </div>
                  </div>
               ))}
            </div>
         </div>
      </section>

      {/* Reader Modal do Artigo */}
      {selectedPost && (
         <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white border border-slate-200 rounded-2xl max-w-2xl w-full p-8 max-h-[85vh] overflow-y-auto">
               <div className="flex justify-between items-center pb-4 border-b border-slate-200 mb-6">
                  <span className="text-xs font-bold text-[#C5A880] uppercase tracking-wider">{selectedPost.category}</span>
                  <button onClick={() => setSelectedPost(null)} className="text-slate-400 hover:text-slate-900 text-xs font-bold uppercase">Fechar ✕</button>
               </div>
               <h3 className="font-heading font-bold text-3xl text-[#0F172A] mb-4 leading-tight">{selectedPost.title}</h3>
               <p className="text-xs text-slate-400 mb-6 pb-4 border-b border-slate-100">Publicado em {selectedPost.date} por {selectedPost.author}</p>
               <div className="prose text-sm text-slate-700 font-light leading-relaxed space-y-4">
                  <p>{selectedPost.content}</p>
                  <p>Para agendar uma avaliação confidencial com nossos pareceristas sobre este tema, submeta os detalhes do seu caso através da nossa plataforma digital.</p>
               </div>
            </div>
         </div>
      )}

      {/* Triagem de Caso Interativa */}
      <section id="triagem" className="py-24 px-8 bg-[#0F172A] text-white border-t border-slate-800">
         <div className="max-w-4xl mx-auto bg-slate-900 p-10 rounded-2xl border border-slate-800">
            <div className="text-center mb-10">
               <span className="text-xs font-bold uppercase tracking-widest text-[#C5A880]">Confidencial & Seguro</span>
               <h3 className="text-3xl font-heading font-bold text-white mt-2">Avaliação Preliminar de Caso</h3>
               <p className="text-xs text-slate-400 font-light mt-2">Selecione a natureza da sua demanda para direcionarmos ao sócio especialista.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
               {['Direito Tributário', 'Holding & Patrimônio', 'Litígio Corporativo'].map(area => (
                  <button 
                     key={area}
                     onClick={() => setIntakeArea(area)}
                     className={`p-4 rounded-xl border text-xs font-bold text-center transition-all ${
                        intakeArea === area 
                           ? 'bg-[#C5A880] text-[#0F172A] border-[#C5A880]' 
                           : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-slate-500'
                     }`}
                  >
                     {area}
                  </button>
               ))}
            </div>

            {intakeArea && (
               <div className="space-y-4 animate-in fade-in duration-300">
                  <input type="text" placeholder="Seu Nome Completo" className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-xs text-white placeholder:text-slate-500 outline-none" />
                  <input type="email" placeholder="E-mail Corporativo ou Pessoal" className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-xs text-white placeholder:text-slate-500 outline-none" />
                  <textarea placeholder="Resumo da situação jurídica ou patrimonial..." className="w-full bg-slate-800 border border-slate-700 rounded-xl p-4 text-xs text-white placeholder:text-slate-500 outline-none h-28 resize-none"></textarea>
                  <button className="w-full bg-[#C5A880] text-[#0F172A] font-black text-xs uppercase tracking-widest py-4 rounded-xl hover:bg-amber-400 transition-colors">
                     Enviar para Análise do Sócio
                  </button>
               </div>
            )}
         </div>
      </section>

      {/* Footer Editorial Nobre */}
      <footer className="bg-[#0F172A] text-white py-12 px-8 border-t border-slate-800 text-xs">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6 text-slate-400">
            <div className="flex items-center gap-3">
               <Scale className="w-5 h-5 text-[#C5A880]" />
               <span className="font-bold text-white text-sm">{prospect.trade_name}</span>
            </div>
            <span>OAB/SP 14.890 • Av. Faria Lima, 3477 - SP</span>
            <span>&copy; {new Date().getFullYear()} YouMachine Advisory Law Engine.</span>
         </div>
      </footer>

    </div>
  );
};
